import React, { useState, useEffect } from 'react';
import { Layout, Typography, Avatar, Dropdown, Switch, Space } from 'antd';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { UserOutlined, LogoutOutlined } from '@ant-design/icons';
import { usePageTitle } from '../../context/PageTitleContext';
import { useTheme } from '../../context/ThemeContext';
import { authService } from '../../services/api';
import './AppHeader.css';

const { Header } = Layout;
const { Title, Text } = Typography;

/**
 * App header component with page title support using PageTitleContext
 */
const AppHeader = () => {
    // Получаем данные из контекста PageTitleContext
    const { pageTitle, rightContent } = usePageTitle();
    const { darkMode, toggleTheme } = useTheme();
    const navigate = useNavigate();
    const currentUser = authService.getCurrentUser();

    useEffect(() => {
        if (pageTitle) {
            document.title = `${pageTitle} - ID`;
        }
    }, [pageTitle]);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [menuCollapsed, setMenuCollapsed] = useState(false);

    // Функция для выхода из системы
    const handleLogout = async () => {
        await authService.logout(navigate);
    };

    // Меню для выпадающего списка
    const userMenu = {
        items: [
            {
                key: '1',
                label: (
                    <div style={{ padding: '4px 0' }}>
                        <Text strong>
                            {currentUser
                                ? `${currentUser.first_name} ${currentUser.last_name || ''}`.trim()
                                : 'Гость'}
                        </Text>

                        <br />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            {currentUser?.email || ''}
                        </Text>
                    </div>
                ),
            },
            {
                key: '2',
                label: (
                    <Space>
                        <Text>{currentUser?.role.name || ''}</Text>
                    </Space>
                ),
            },
            {
                key: '3',
                label: (
                    <Space>
                        <Text>Тёмная тема</Text>
                        <Switch
                            checked={darkMode}
                            onChange={toggleTheme}
                            size="small"
                        />
                    </Space>
                ),
            },
            {
                type: 'divider',
                key: 'divider',
                style: { display: currentUser ? 'block' : 'none' }
            },
            {
                key: '4',
                danger: true,
                icon: <LogoutOutlined />,
                label: 'Выйти',
                onClick: handleLogout,
                style: { display: currentUser ? 'block' : 'none' }
            },
        ],
    };

    // Слушаем изменения размера окна
    useEffect(() => {
        // Функция проверки размера окна
        const checkScreenSize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        // Подписываемся на событие изменения размера окна
        window.addEventListener('resize', checkScreenSize);

        // Обратный вызов для очистки обработчика событий
        return () => {
            window.removeEventListener('resize', checkScreenSize);
        };
    }, []);

    // Определяем, показывать ли логотип
    const showLogo = !isMobile && !menuCollapsed;

    //Показываем инициалы на аватарке
    const getInitials = (user) => {
        if (!user?.first_name) return 'U'; // Если нет имени (маловероятно), ставим "U"
        const firstInitial = user.first_name.charAt(0).toUpperCase();
        const lastInitial = user.last_name ? user.last_name.charAt(0).toUpperCase() : '';
        return firstInitial + lastInitial;
    };

    return (
        <>
            {pageTitle && (
                <Helmet>
                    <title>{pageTitle}</title>
                </Helmet>
            )}
            <Header className="app-header">
                {/* Left corner SVG */}
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="32"
                    height="32"
                    viewBox="0 0 32 32"
                    fill="none"
                    className="app-header__corner app-header__corner--left"
                >
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M0 32V0H32C24.5602 0 20.8403 0 17.7883 0.817781C9.50613 3.03699 3.03699 9.50614 0.81778 17.7883C0 20.8403 0 24.5602 0 32Z"
                        fill="currentColor"
                    />
                </svg>
                {/* Logo Box - показываем только если не мобильное устройство и меню не свернуто */}
                {showLogo && (
                    <div className="app-header__extra-content_placement_left">
                        <svg
                            width={86}
                            height={32}
                            viewBox="0 0 86 32"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="app-header__logo"
                        >
                            <path xmlns="http://www.w3.org/2000/svg"
                                  d="M41.8242 22.9992V7.1592H47.7762C49.4402 7.1592 50.8082 7.6312 51.8802 8.5752C52.9522 9.5192 53.4882 10.7512 53.4882 12.2712C53.4882 13.7752 52.9522 15.0072 51.8802 15.9672C50.8082 16.9272 49.4402 17.4072 47.7762 17.4072H45.0642V22.9992H41.8242ZM45.0642 14.5752H47.7762C48.5282 14.5752 49.1122 14.3672 49.5282 13.9512C49.9602 13.5192 50.1762 12.9592 50.1762 12.2712C50.1762 11.5992 49.9522 11.0552 49.5042 10.6392C49.0722 10.2232 48.4962 10.0152 47.7762 10.0152H45.0642V14.5752Z"
                                  fill="white"/>
                            <path xmlns="http://www.w3.org/2000/svg"
                                  d="M55.1622 15.0792C55.1302 12.8232 55.9462 10.8792 57.6102 9.2472C59.2902 7.6152 61.2742 6.8232 63.5622 6.8712C65.8502 6.8232 67.8342 7.6152 69.5142 9.2472C71.2102 10.8792 72.0422 12.8232 72.0102 15.0792C72.0422 17.3352 71.2102 19.2792 69.5142 20.9112C67.8342 22.5432 65.8502 23.3352 63.5622 23.2872C61.2742 23.3352 59.2902 22.5432 57.6102 20.9112C55.9462 19.2792 55.1302 17.3352 55.1622 15.0792ZM68.6742 15.0792C68.6742 13.5752 68.1862 12.3352 67.2102 11.3592C66.2502 10.3672 65.0422 9.8712 63.5862 9.8712C62.1142 9.8712 60.8982 10.3592 59.9382 11.3352C58.9782 12.3112 58.4982 13.5592 58.4982 15.0792C58.4982 16.5992 58.9782 17.8472 59.9382 18.8232C60.8982 19.7992 62.1142 20.2872 63.5862 20.2872C65.0422 20.2872 66.2502 19.7992 67.2102 18.8232C68.1862 17.8472 68.6742 16.5992 68.6742 15.0792Z"
                                  fill="white"/>
                            <path xmlns="http://www.w3.org/2000/svg"
                                  d="M73.6499 19.7832L75.8819 18.1032C77.0019 19.6392 78.2979 20.4072 79.7699 20.4072C80.4739 20.4072 81.0259 20.2552 81.4259 19.9512C81.8259 19.6312 82.0259 19.1992 82.0259 18.6552C82.0259 18.2072 81.8899 17.8632 81.6179 17.6232C81.3459 17.3672 80.8099 17.0552 80.0099 16.6872L78.3059 15.9192C75.7459 14.7832 74.4659 13.2632 74.4659 11.3592C74.4659 10.0152 74.9619 8.9352 75.9539 8.1192C76.9459 7.2872 78.2019 6.8712 79.7219 6.8712C80.9059 6.8712 81.9459 7.1432 82.8419 7.6872C83.7379 8.2152 84.4419 8.9432 84.9539 9.8712L82.7459 11.5752C81.9299 10.3592 80.9219 9.7512 79.7219 9.7512C79.1459 9.7512 78.6739 9.8872 78.3059 10.1592C77.9379 10.4312 77.7539 10.8072 77.7539 11.2872C77.7539 11.6872 77.8819 12.0152 78.1379 12.2712C78.4099 12.5112 78.8979 12.7912 79.6019 13.1112L81.4019 13.9032C82.7619 14.5112 83.7539 15.1752 84.3779 15.8952C85.0019 16.5992 85.3139 17.4792 85.3139 18.5352C85.3139 19.9592 84.7939 21.1112 83.7539 21.9912C82.7139 22.8552 81.3859 23.2872 79.7699 23.2872C78.4419 23.2872 77.2419 22.9752 76.1699 22.3512C75.1139 21.7272 74.2739 20.8712 73.6499 19.7832Z"
                                  fill="white"/>
                            <path xmlns="http://www.w3.org/2000/svg"
                                  d="M22.7097 0H9.29032C4.15942 0 0 4.15942 0 9.29032V22.7097C0 27.8406 4.15942 32 9.29032 32H22.7097C27.8406 32 32 27.8406 32 22.7097V9.29032C32 4.15942 27.8406 0 22.7097 0Z"
                                  fill="#05D32A"/>
                            <path xmlns="http://www.w3.org/2000/svg"
                                  d="M25 15.5H7V24C7 24.8284 7.67157 25.5 8.5 25.5H23.5C24.3284 25.5 25 24.8284 25 24V15.5ZM23 20.5V23.5H9V20.5H23ZM28 24C28 26.4853 25.9853 28.5 23.5 28.5H8.5C6.01472 28.5 4 26.4853 4 24V12.5H8V9.5H11V12.5H21V6.5H8V3.5H24V12.5H28V24Z"
                                  fill="#17181A"/>
                        </svg>
                    </div>
                )}

                {/* Header content container with page title and right content */}
                <div className="app-header__content">
                    {pageTitle && (
                        <Title className="app-header__title" level={5}>
                            {pageTitle}
                        </Title>
                    )}

                    <div className="app-header__right-content">
                        {rightContent && (
                            <div className="app-header__right-actions">
                                {rightContent}
                            </div>
                        )}
                    </div>
                </div>
                {/* Аватарка пользователя с выпадающим меню */}
                <Dropdown menu={userMenu} placement="bottomRight" arrow>
                    <Avatar size={40} className={currentUser ? "app-header__avatar-user" : "app-header__avatar-guest"}>
                        {currentUser ? (
                            getInitials(currentUser) // Показываем инициалы
                        ) : (
                            <UserOutlined/> // Если не авторизован, показываем иконку
                        )}
                    </Avatar>
                </Dropdown>


                {/* Right corner SVG */}
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="32"
                    height="32"
                    viewBox="0 0 32 32"
                    fill="none"
                    className="app-header__corner app-header__corner--right"
                >
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M0 32V0H32C24.5602 0 20.8403 0 17.7883 0.817781C9.50613 3.03699 3.03699 9.50614 0.81778 17.7883C0 20.8403 0 24.5602 0 32Z"
                        fill="currentColor"
                    />
                </svg>
            </Header>
        </>
    );
};

export default AppHeader;